import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, setUserId } from "firebase/analytics";
import {
  AccountClickHit,
  AccountHit,
  AnalyticsKeywordSource,
  BannerTrackingHit,
  CategoryHit,
  CategoryTrackingElement,
  isPerkClickHit,
  NewsletterHit,
  NotificationClickHit,
  PageName,
  PageTrackingHit,
  PaginationHit,
  PerkClickHit,
  PerkSearchHit,
  PerkSource,
  PerkTrackingElement,
  Redemption,
  RegisterUserAccountHit,
  SearchTrackingHit,
  Source,
  TrackingAction,
  TrackingElement,
  TrackingId,
  TrackingPayload,
  ViewAllHit,
  SortType,
  WebExtensionElement,
  PopupClickHit,
  AlertBannerClickHit,
  GiftCardButtonHit,
  WeeklyFlyerTopButtonHit,
  WeeklyFlyerFooterButtonHit,
  TFLSearchWidgetHit,
  TFLSearchResultHit,
  ADTravelWidgetHit,
  Country,
  NewsletterUnsubscribeHit,
} from 'lib/workperks/models/Analytics';
import Location from 'lib/workperks/models/Location';
import Site from 'lib/workperks/models/Site';
import Notification, { NotificationMeta } from 'lib/workperks/models/Notification';
import Perk from 'lib/workperks/models/Perk';
import Popup, { PopupMeta } from 'lib/workperks/models/Popup';
import { Promobar, PromobarMeta } from 'lib/workperks/models/AlertBanner';
import Account from 'lib/workperks/models/Perk';
import User from 'lib/workperks/models/User';
import { FIREBASE_CONFIG, POC_FIREBASE_CONFIG, POC_FIREBASE_ENABLED, POC_FIREBASE_MULTIPLIER } from 'lib/workperks/common/constants';
import { detect } from 'detect-browser';
import LibSite from 'lib/Site';
import { VenngoRedemptionType } from "lib/workperks/models/RedemptionOptions";

export const getBrowserInfo = detect();

/**
 * This service's state is always set by `analyticsMiddleware`
 *
 * {@link analyticsMiddleware}
 */
class AnalyticsService {
  private readonly TYPE_ID = 37;
  private user!: User;
  private site!: Site;
  private location!: Location;
  private debug = true;
  private firebaseApp = null;
  private pocFirebaseApp = null;
  private pageName: PageName;
  private templateId: number | null;
  private searchQuery: string;

  setUser(user: User) {
    this.user = user;
    return this;
  }

  setSite(site: Site) {
    this.site = site;
    return this;
  }

  setLocation(location: Location) {
    this.location = location;
    return this;
  }

  setDebugMode(value: boolean) {
    this.debug = value;
    return this;
  }

  setPageName(value: string) {
    this.pageName = PageName[value];
    return this;
  }

  setTemplateId(value: number | null) {
    this.templateId = value ?? null;
    return this;
  }

  get isInitialized() {
    return this.site !== undefined && this.user !== undefined;
  }

  get debugMode() {
    return this.site !== undefined;
  }

  get locationType() {
    return this.location.id === 0 ? 'GPS' : this.location.id ? 'Saved' : 'Temporary';
  }

  track = (id: TrackingId, params: TrackingPayload) => {
    if (!this.isInitialized) return console.warn('AnalyticsService -- Not initialized. Skipping tracking.');

    params = this.mapParameters(params);

    const event = {
      key_id: id,
      type_id: this.TYPE_ID,
      site_id: this.site.id,
      user_ID: this.user.id,
      page_name: PageName[this.pageName],
      location_type: this.locationType,
      location_latitude: this.location.latitude,
      location_longitude: this.location.longitude,
      location_country: this.location.country,
      ...params,
    };

    if (this.templateId !== null && this.templateId !== undefined) {
      event['promotion_id'] = this.templateId;
    }

    if (!event["source"]) {
      event["source"] = this.getBrowserType();
    }

    Object.keys(event).forEach(key => {
      if (event[key] === null) {
        delete event[key];
      }
    });
    const analytics = getAnalytics(this.firebaseApp);
    const pocAnalytics = getAnalytics(this.pocFirebaseApp);

    setUserId(analytics, (this.user.id).toString());
    logEvent(analytics, TrackingId[id], event);
    if (POC_FIREBASE_ENABLED === true && POC_FIREBASE_MULTIPLIER > 0) {
      setUserId(pocAnalytics, (this.user.id).toString());
      for (let i = 0; i < POC_FIREBASE_MULTIPLIER; i++) {
        event['identifier'] = 'POC FIREBASE - ' + new Date().toISOString() + ' - ' + (i + 1);
        logEvent(pocAnalytics, TrackingId[id], event);
        // console.log(JSON.stringify(Object.assign(
        //   {
        //     type: 'AnalyticsHit',
        //     event: TrackingId[id],
        //     ...event,
        //   }
        // )));
      }
    }

    if (this.debug) {
      console.log(JSON.stringify(Object.assign(
        {
          type: 'AnalyticsHit',
          event: TrackingId[id],
          ...event,
        }
      )));
    }
    return null;
  };

  trackRegistration = async (id: TrackingId, params: RegisterUserAccountHit) => {
    const pageName = params.page_name

    const event = {
      key_id: id,
      type_id: this.TYPE_ID,
      page: PageName[pageName],
      source: this.getBrowserType(),
      ...params,
    };

    const analytics = getAnalytics(this.firebaseApp);

    // @ts-expect-error - Expected error
    if (params.user_id) {
      setUserId(analytics, (this.user.id).toString());
    }
    logEvent(analytics, TrackingId[id], event);

    if (this.debugMode) {
      console.log(JSON.stringify(Object.assign(
        {
          type: 'AnalyticsHit',
          event: TrackingId[id],
          ...event,
        }
      )));
    }
  };

  initialize = (site: Site, user: User, location: Location) => {
    if (site) {
      this.setSite(site);
    }
    if (user) {
      this.setUser(user);
    }
    if (location) {
      this.setLocation(location);
    }

    if (!this.pageName) {
      const pageIndex: string = window.s.pageName;
      this.pageName = PageName[pageIndex] ?? null;
    }

    this.firebaseApp = initializeApp(FIREBASE_CONFIG);
    this.pocFirebaseApp = initializeApp(POC_FIREBASE_CONFIG, "dummyApp");
    this.setDebugMode(LibSite.getCurrentSite().isTrackingLogEnabled);

    return this;
  };

  mapParameters = (params: TrackingPayload) => {
    if (isPerkClickHit(params) && !params.campaign_id) params.campaign_id = params.perk_id;
    return params;
  };

  trackBannerHit = (
    action: string = TrackingAction.Loaded,
    perk: Perk,
    index: string | number = 0,
    elementName: PerkTrackingElement = PerkTrackingElement.Banner,
  ) => {
    const trackingId = TrackingId.PerkInteraction;

    if (!trackingId) {
      console.warn(`AnalyticsService@trackBannerHitByRoute: Could not find tracking ID for route '${this.pageName}'`);
      if (this.debugMode) throw Error(`Tracking ID is not mapped for route '${this.pageName}'`);
      return;
    }

    const payload: BannerTrackingHit = {
      perk_id: perk.id,
      account_id: perk.accountId ?? null,
      position: Number(index),
      element_name: elementName,
      perk_source: this.getObjectSource(perk),
      action,
      campaign_id: perk.campaign_id ?? null
    };

    if (perk.searchQuery) payload.keyword = perk.searchQuery;
    if (perk.category?.id) payload.category_id = Number(perk.category.id);
    if (perk.subcategory?.id) payload.subcategory_id = Number(perk.subcategory.id);
    if (!this.pageName) this.setPageName(window.s.pageName);

    if (perk.promotion) payload.promotion_id = perk.promotion.id;

    this.track(trackingId, payload);

  };

  trackPageView = (trackingPayload) => {
    const trackingId = TrackingId.PageView;

    let payload: PageTrackingHit = {};

    if (!this.pageName) this.setPageName(window.s.pageName);

    if (trackingPayload) {
      payload = {
        popup_id: trackingPayload["popup_id"] ? parseInt(trackingPayload["popup_id"]) : null,
        promotion_id: trackingPayload["promotion_id"] ? parseInt(trackingPayload["promotion_id"]) : null,
        source: parseInt(trackingPayload["wbeId"]) ? Source.WebBrowserExtension : null,
        tag_id: trackingPayload["tag"] ? parseInt(trackingPayload["tag"]) : null,
        category_id: trackingPayload["category_id"] ? parseInt(trackingPayload["category_id"]) : null,
        subcategory_id: trackingPayload["subcategory_id"] ? parseInt(trackingPayload["subcategory_id"]) : null,
        perk_id: trackingPayload["perk_id"] ? parseInt(trackingPayload["perk_id"]) : null,
        campaign_id: trackingPayload["campaign_id"] ? parseInt(trackingPayload["campaign_id"]) : null,
        account_id: trackingPayload["account_id"] ? parseInt(trackingPayload["account_id"]) : null,
        perk_source: trackingPayload["perk_source"] ? trackingPayload["perk_source"] : null,
        edition_id: trackingPayload?.["edition"] ? parseInt(trackingPayload["edition"]) : null,
        alert_banner_id: trackingPayload?.["alert_banner_id"] ? parseInt(trackingPayload["alert_banner_id"]) : null,
        spinwheel_id: trackingPayload?.["spinwheel_id"] ? parseInt(trackingPayload["spinwheel_id"]) : null,
        keyword: trackingPayload["keyword"] ? trackingPayload["keyword"] : null,
        position: trackingPayload["position"] ? parseInt(trackingPayload["position"]) : null,
      };

      if (trackingPayload["notification_id"]) {
        payload.notification_id = trackingPayload?.["notification_id"] ? parseInt(trackingPayload["notification_id"]) : null;
        payload.notification_status = trackingPayload?.["notification_status"] ? trackingPayload["notification_status"] : null;
        payload.notification_type = trackingPayload?.["notification_type"] ? trackingPayload["notification_type"] : null;
        payload.promotion_id = trackingPayload?.["promotion_id"] ? parseInt(trackingPayload["promotion_id"]) : null;
      }

      if (trackingPayload["keyword"] && this.pageName != PageName.Search) {
        payload.sponsored_keyword = (this.location.country !== Country.US && trackingPayload["sponsored_keyword"]) ? parseInt(trackingPayload["sponsored_keyword"]) : 0;
        payload.sponsored_result = (this.location.country !== Country.US && trackingPayload["sponsored_result"]) ? parseInt(trackingPayload["sponsored_result"]) : 0;
      }
    }

    this.track(trackingId, payload);
    return null;
  };

  trackWebExtensionHit = (trackingPayload) => {
    const trackingId = TrackingId.PerkInteraction;

    if (!trackingPayload) {
      throw Error("Tracking Payload is required.");
    }

    const wbeId = parseInt(trackingPayload["wbeId"]);

    if (!(wbeId === TrackingId.WBEPopupClick || wbeId === TrackingId.WBEPageNotificationClick)) {
      throw Error("wbeId Parameter is incorrect.");
    }
    const payload: PerkClickHit = {
      promotion_id: trackingPayload["promotion_id"] ? parseInt(trackingPayload["promotion_id"]) : null,
      keyword: trackingPayload["keyword"] ? trackingPayload["keyword"] : null,
      category_id: trackingPayload["category_id"] ? parseInt(trackingPayload["category_id"]) : null,
      subcategory_id: trackingPayload["subcategory_id"] ? parseInt(trackingPayload["subcategory_id"]) : null,
      perk_id: trackingPayload["perk_id"] ? parseInt(trackingPayload["perk_id"]) : null,
      campaign_id: trackingPayload["campaign_id"] ? parseInt(trackingPayload["campaign_id"]) : null,
      account_id: trackingPayload["account_id"] ? parseInt(trackingPayload["account_id"]) : null,
      source: Source.WebBrowserExtension,
      action: TrackingAction.Click,
      element_name: wbeId === TrackingId.WBEPopupClick ? WebExtensionElement.WBEPopupClick : WebExtensionElement.WBEPageNotificationClick,
      perk_source: trackingPayload["perk_source"] ? trackingPayload["perk_source"] : null
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };


  // TODO: Verify firebase automatically tracks newsletter or we need to keep tracking it in the code
  trackNewsletterHit = (
    trackingPayload,
    perk: Perk) => {
    const trackingId = TrackingId.PerkInteraction;

    const payload: PerkClickHit = {
      perk_id: perk.id,
      account_id: perk.accountId ?? null,
      campaign_id: perk.campaign_id ?? null,
      position: trackingPayload?.["position"] ? parseInt(trackingPayload["position"]) : 1,
      category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
      subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
      perk_source: this.getObjectSource(perk),
      source: Source.Newsletter,
      action: TrackingAction.Click,
      edition_id: trackingPayload?.["edition"] ? parseInt(trackingPayload["edition"]) : null,
      element_name: trackingPayload?.["element"] ? trackingPayload["element"] : null,
      newsletter_type: trackingPayload?.["type"] ? trackingPayload["type"] : null,
      promotion_id: trackingPayload["promotion_id"] ? parseInt(trackingPayload["promotion_id"]) : null,
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;

  };

  trackReferralLinkClick = (
    perk: Perk) => {
    const trackingId = TrackingId.ReferralLinks;

    const payload: NewsletterHit = {
      perk_id: perk.id,
      account_id: perk.accountId ?? null,
      category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
      subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
      perk_source: this.getObjectSource(perk),
      action: TrackingAction.Click
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;

  };

  trackCategoryClick = (
    categoryId: number,
    elementName: CategoryTrackingElement = CategoryTrackingElement.Menu) => {
    const trackingId = TrackingId.CategoryClick;

    const payload: CategoryHit = {
      category_id: categoryId,
      element_name: elementName
    };

    if (!this.pageName) this.setPageName(window.s.pageName);
    this.track(trackingId, payload);
    return null;
  };

  trackViewAllClick = (
    action: string = TrackingAction.Click,
    elementName: TrackingElement = TrackingElement.PromotionViewAll,
    tracking: number = TrackingId.PromotionViewAll,
    source: string) => {
    const trackingId = tracking;

    const payload: ViewAllHit = {
      action,
      element_name: elementName,
      perk_source: source
    };

    if (!this.pageName) this.setPageName(window.s.pageName);
    this.track(trackingId, payload);
    return null;
  };

  trackPagination = (pageStartIndex: number) => {
    const trackingId = TrackingId.Pagination;

    const payload: PaginationHit = {
      element_name: TrackingElement.Pagination,
      position: pageStartIndex
    };

    if (!this.pageName) this.setPageName(window.s.pageName);
    this.track(trackingId, payload);
    return null;
  };

  trackPerkSearch = (
    perk: Perk,
    action: string = TrackingAction.Loaded) => {
    const trackingId = TrackingId.PerkInteraction;

    const payload: PerkSearchHit = {
      perk_id: perk.id,
      account_id: perk.accountId ?? null,
      element_name: 'ShortSearch',
      category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
      subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
      perk_source: this.getObjectSource(perk),
      action,
      campaign_id: perk.campaign_id ?? null,
      promotion_id: perk.promotion?.id ? perk.promotion.id : null,
      keyword: perk.keyword ?? perk.searchQuery ?? null,
      position: perk.position ?? null,
      sponsored_keyword: (this.location.country !== Country.US && perk.sponsored_keyword) ? perk.sponsored_keyword : 0,
      sponsored_result: (this.location.country !== Country.US && perk.sponsored_result) ? perk.sponsored_result : 0,
    };
    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackSearchQuery = (
    keyword: string,
    source: string,
    keywordSource: AnalyticsKeywordSource,
    distance: number | string,
    filters: object = null
  ) => {

    const payload: SearchTrackingHit = {
      keyword,
      action: TrackingAction.Submit,
      source: 'web',
      keyword_source: keywordSource,
      distance,
      perk_source: source,
    };

    if (filters) {
      // @ts-expect-error - Expected error
      payload['expiring_filter'] = !!filters.showExpiringOnly;
      // @ts-expect-error - Expected error
      payload['new_filter'] = !!filters.showNewOnly;
      // @ts-expect-error - Expected error
      payload['instore_filter'] = filters.redemptionFilter.includes('instore');
      // @ts-expect-error - Expected error
      payload['online_filter'] = filters.redemptionFilter.includes('online');
      // @ts-expect-error - Expected error
      payload['mobile_filter'] = filters.redemptionFilter.includes('mobile');
      // @ts-expect-error - Expected error
      payload['email_filter'] = filters.redemptionFilter.includes('email');
      // @ts-expect-error - Expected error
      payload['sort_type'] = filters.sort !== 0 ? SortType[filters.sort] : null;

    }

    return this.track(TrackingId.SearchSubmission, payload);
  };

  trackAccountCard = (
    account: Account,
    action: TrackingAction = TrackingAction.Loaded,
  ) => {
    const trackingId = TrackingId.AccountInteraction;

    const payload: AccountHit = {
      account_id: account.id ?? null,
      action,
      element_name: 'AccountCard',
      source: 'web',
      account_source: account.source
    };

    if (!this.pageName) this.setPageName(window.s.pageName);
    this.track(trackingId, payload);
    return null;
  };

  trackPerkCardImpression = (
    action: TrackingAction = TrackingAction.Loaded,
    perk: Perk,
    index: string | number = 1,
    elementName: PerkTrackingElement = PerkTrackingElement.PerkCard,
  ) => {
    if (!perk || !perk.id) {
      return;
    }

    const trackingId = TrackingId.PerkInteraction;

    //
    const payload: PerkClickHit = {
      perk_id: perk.id,
      account_id: perk.accountId ?? null,
      campaign_id: perk.campaign_id ?? null,
      element_name: elementName,
      category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
      subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
      perk_source: this.getObjectSource(perk),
      keyword: perk.keyword ?? perk.searchQuery ?? null,
      position: perk.index ? perk.index : Number(index) + 1,
      action
    };

    if (perk.searchQuery) {
      payload.sponsored_keyword = (this.location.country !== Country.US && perk.sponsored_keyword) ? perk.sponsored_keyword : 0;
      payload.sponsored_result = (this.location.country !== Country.US && perk.sponsored_result) ? perk.sponsored_result : 0;
    }

    if (perk.promotion) payload.promotion_id = perk.promotion.id;

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackPopupImpression = (
    action: TrackingAction = TrackingAction.Loaded,
    popup: Popup,
    meta: PopupMeta,
    element_name: TrackingElement = TrackingElement.Popup,
    editionId?: number,
  ) => {
    if (!popup?.id) {
      return;
    }

    const trackingId = TrackingId.PopUpInteraction;
    const promotionId = [1, 2, 5].includes(Number(popup.type?.id)) ? meta.promotion_id : null;
    const payload: PopupClickHit = {
      popup_id: popup.id,
      action,
      element_name,
      perk_id: meta.campaign ? meta.campaign.perk_id : null,
      account_id: meta.campaign ? meta.campaign.account_id : null,
      campaign_id: meta.campaign ? meta.campaign.campaign_id : null,
      category_id: meta.campaign ? meta.campaign.category_id : null,
      subcategory_id: meta.campaign ? meta.campaign.subcategory_id : null,
      promotion_id: promotionId,
      perk_source: meta.campaign ? meta.campaign.source : null,
      spinwheel_id: meta.spinwheel_id ? meta.spinwheel_id : null,
      edition_id: editionId ? Number(editionId) : null,
      template_type: popup.template ? Number(popup.template.id) : null,
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackAlertBannerImpression = (
    action: TrackingAction = TrackingAction.Loaded,
    data: Promobar,
    meta: PromobarMeta,
    element_name: TrackingElement = TrackingElement.AlertBanner,
    editionId?: number,
    emailId?: number,
  ) => {
    if (!data || !data.id) {
      return;
    }
    const trackingId = TrackingId.AlertBannerInteraction;
    const promotionId = meta.promotion_id ? meta.promotion_id : null;

    const payload: AlertBannerClickHit = {
      alert_banner_id: data.id,
      action,
      element_name,
      perk_id: meta.campaign ? meta.campaign.perk_id : null,
      account_id: meta.campaign ? meta.campaign.account_id : null,
      campaign_id: meta.campaign ? meta.campaign.campaign_id : null,
      category_id: meta.campaign ? meta.campaign.category_id : null,
      subcategory_id: meta.campaign ? meta.campaign.subcategory_id : null,
      promotion_id: promotionId,
      perk_source: meta.campaign ? meta.campaign.source : null,
      edition_id: editionId ? Number(editionId) : null,
      template_type: data.template.id,
      email_id: emailId ? Number(emailId) : null,
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackUserRegistration = (
    action: TrackingAction = TrackingAction.Loaded,
    register: object | null,
    step: number = 1,
    pageName: string,
    elementName: TrackingElement = TrackingElement.UserAccountRegistration
  ) => {
    const trackingId = TrackingId.UserRegistrationInteraction;

    const payload: RegisterUserAccountHit = {
      // @ts-expect-error - Expected error
      site_id: register.site_id ?? null,
      // @ts-expect-error - Expected error
      user_ID: register.user_id ?? null,
      // @ts-expect-error - Expected error
      family_invited: register.family_invited ?? false,
      // @ts-expect-error - Expected error
      email: register.email ?? null,
      step,
      action,
      element_name: elementName,
      page_name: pageName,
    };

    return this.trackRegistration(trackingId, payload);
  };

  trackAccountImpression = (
    action: TrackingAction = TrackingAction.Loaded,
    account: Account,
    index: string | number = 0,
    elementName: PerkTrackingElement = PerkTrackingElement.TopBrand
  ) => {
    const trackingId = TrackingId.AccountInteraction;

    const payload: AccountClickHit = {
      account_id: account.id,
      position: Number(index) + 1,
      element_name: elementName,
      category_id: window.s.search?.selectedParentCategory ? Number(window.s.search.selectedParentCategory.id) : null,
      account_source: this.getObjectSource(account), // add ad or es in top brands
      action,
      source: Source.Web
    };

    if (!this.pageName) this.setPageName(window.s.pageName)

    this.track(trackingId, payload);
    return null;
  };
  getObjectSource = (object) => {
    if (!object.licenses) return PerkSource.VenngoCA;
    if (object.licenses.includes(2)) {
      return PerkSource.VenngoUS;
    } else if (object.licenses.includes(3)) {
      return PerkSource.AccessUS;
    } else {
      return PerkSource.VenngoCA;
    }
  }
  getBrowserType = () => {
    if (window.s.isMobile) {
      // This if block is for the web views that opens in the mobile app
      return Source.Mobile;
    } else if (/Android OS|iOS/i.test(getBrowserInfo.os) || window.s.redirectMobile) {
      return Source.MobileWeb;
    }
    return Source.Web;
  }

  trackNotificationImpression = (
    action: TrackingAction = TrackingAction.Loaded,
    notification: Notification | null,
    meta?: NotificationMeta,
    element_name: TrackingElement = TrackingElement.NotificationPanel,
  ) => {
    const trackingId = TrackingId.NotificationInteraction;

    const payload: NotificationClickHit = {
      notification_id: notification.id ?? null,
      action,
      element_name,
      notification_status: notification?.status?.id ? String(notification.status.id) : null,
      source: this.getBrowserType(),
      notification_type: notification?.type?.id ? String(notification.type.id) : null,
      // meta
      perk_id: meta?.campaign ? meta.campaign.perk_id : null,
      account_id: meta?.campaign ? meta.campaign.account_id : null,
      campaign_id: meta?.campaign ? meta.campaign.campaign_id : null,
      category_id: meta?.campaign ? meta.campaign.category_id : null,
      subcategory_id: meta?.campaign ? meta.campaign.subcategory_id : null,
      promotion_id: meta?.promotion_id || null,
      perk_source: meta?.campaign ? meta.campaign.source : null,
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackPerkRedemption = (
    perk: Perk,
    redemptionType,
    trackingPayload
  ) => {

    const trackingId = TrackingId.Redemption;

    if (!trackingId) {
      console.warn(`AnalyticsService@trackPerkRedemption: Could not find tracking ID for route '${this.pageName}'`);
      if (this.debugMode) throw Error(`Tracking ID is not mapped for route '${this.pageName}'`);
      return;
    }

    const payload: Redemption = {
      perk_id: perk.id, // Perk ID
      campaign_id: perk.campaign_id, // Campaign ID
      account_id: perk.accountId, // Account ID
      // @ts-expect-error - Expected error
      redemption_type: VenngoRedemptionType[redemptionType], // Specifies the type of redemption that occurred.
      perk_source: this.getObjectSource(perk),
      category_id: isNaN(Number(perk.category.id)) ? null : Number(perk.category.id),
      subcategory_id: isNaN(Number(perk.subcategory.id)) ? null : Number(perk.subcategory.id),
      promotion_id: perk.promotionId ?? null,
      edition_id: trackingPayload?.["edition"] ? parseInt(trackingPayload["edition"]) : null,
      keyword: trackingPayload["keyword"] ? trackingPayload["keyword"] : null,
      position: trackingPayload["position"] ? parseInt(trackingPayload["position"]) : null,
      alert_banner_id: trackingPayload?.["alert_banner_id"] ? parseInt(trackingPayload["alert_banner_id"]) : null,
    };

    if (trackingPayload["keyword"]) {
      payload.sponsored_keyword = (this.location.country !== Country.US && trackingPayload["sponsored_keyword"]) ? parseInt(trackingPayload["sponsored_keyword"]) : 0;
      payload.sponsored_result = (this.location.country !== Country.US && trackingPayload["sponsored_result"]) ? parseInt(trackingPayload["sponsored_result"]) : 0;
    }

    if (trackingPayload?.["popup_id"]) {
      payload.popup_id = trackingPayload?.["popup_id"] ? parseInt(trackingPayload["popup_id"]) : null;
    }

    if (trackingPayload?.["notification_id"]) {
      payload.notification_id = trackingPayload?.["notification_id"] ? parseInt(trackingPayload["notification_id"]) : null;
      payload.notification_status = trackingPayload?.["notification_status"] ? trackingPayload["notification_status"] : null;
      payload.notification_type = trackingPayload?.["notification_type"] ? trackingPayload["notification_type"] : null;
    }

    if (!this.pageName) this.setPageName(window.s.pageName)
    this.track(trackingId, payload)
  };

  trackStoryHit = (
    slide,
    index: string | number = 0,
  ) => {
    const trackingId = TrackingId.StoryClick;

    if (!trackingId) {
      console.warn(`AnalyticsService@trackBannerHitByRoute: Could not find tracking ID for route '${this.pageName}'`);
      if (this.debugMode) throw Error(`Tracking ID is not mapped for route '${this.pageName}'`);
      return;
    }

    const payload = {
      element_name: PerkTrackingElement.Story,
      perk_id: slide.perk.id,
      account_id: slide.perk.accountId ?? null,
      position: Number(index),
      perk_source: this.getObjectSource(slide.perk),
      campaign_id: slide.perk.campaign_id ?? null,
      ...(slide.perk.category?.id && { category_id: Number(slide.perk.category.id) }),
      ...(slide.perk.subcategory?.id && { subcategory_id: Number(slide.perk.subcategory.id) }),
      ...(slide.perk.promotion?.id && { promotion_id: slide.perk.promotion?.id }),
      ...(slide.title && { promotion: slide.title }),
    };

    this.track(trackingId, payload);

  };


  trackGiftCardButton = (
    action: TrackingAction = TrackingAction.Click
  ) => {
    const trackingId = TrackingId.GiftCardButtonInteraction;

    const payload: GiftCardButtonHit = {
      action,
      element_name: TrackingElement.GiftCardButton,
      source: this.getBrowserType()
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackWeeklyFlyerTopButton = (
    action: TrackingAction = TrackingAction.Click
  ) => {
    const trackingId = TrackingId.WeeklyFlyerInteraction;

    const payload: WeeklyFlyerTopButtonHit = {
      action,
      element_name: TrackingElement.WeeklyFlyerTopButton,
      source: this.getBrowserType()
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackWeeklyFlyerFooterButton = (
    action: TrackingAction = TrackingAction.Click
  ) => {
    const trackingId = TrackingId.WeeklyFlyerInteraction;

    const payload: WeeklyFlyerFooterButtonHit = {
      action,
      element_name: TrackingElement.WeeklyFlyerFooterButton,
      source: this.getBrowserType()
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackTFLSearchWidgetEvent = (
    keyword: string,
    ticket_title: string,
    action: TrackingAction = TrackingAction.Loaded,
  ) => {
    const trackingId = TrackingId.TicketsForLessInteraction;

    const payload: TFLSearchWidgetHit = {
      action,
      element_name: TrackingElement.TFLSearchWidget,
      source: this.getBrowserType(),
      keyword,
      ticket_title,
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackADTravelPageLoadEvent = (
    keyword: string,
    action: TrackingAction = TrackingAction.Loaded,
  ) => {
    const trackingId = TrackingId.TicketsForLessInteraction;

    const payload: TFLSearchWidgetHit = {
      action,
      element_name: TrackingElement.TFLSearchWidget,
      source: this.getBrowserType(),
      keyword,
      ticket_title,
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackADTravelWidgetClick = (
    element_name: TrackingElement.ADTravelFlightsExplore | TrackingElement.ADTravelHotelsSearch,
    action: TrackingAction = TrackingAction.Click,
  ) => {
    const trackingId = TrackingId.ADTravelWidgetInteraction;

    const payload: ADTravelWidgetHit = {
      action,
      element_name,
      source: this.getBrowserType(),
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackTFLSearchResultClick = (
    keyword: string,
    ticket_title: string,
    action: TrackingAction = TrackingAction.Click
  ) => {
    const trackingId = TrackingId.TicketsForLessInteraction;

    const payload: TFLSearchResultHit = {
      action,
      element_name: TrackingElement.TFLSearchResult,
      source: this.getBrowserType(),
      keyword,
      ticket_title,
    };

    if (!this.pageName) this.setPageName(window.s.pageName);

    this.track(trackingId, payload);
    return null;
  };

  trackNewsletterUnsubscribe = (
    action: TrackingAction,
    userId: number,
    newsletterEmail: string,
    editionId: string | number | null,
  ) => {
    if (!this.isInitialized) {
      this.user = { id: userId } as User;
      this.site = {} as Site;
      this.location = { id: 0, latitude: null, longitude: null, country: null } as unknown as Location;
      this.firebaseApp = initializeApp(FIREBASE_CONFIG);
      this.pocFirebaseApp = initializeApp(POC_FIREBASE_CONFIG, "dummyApp");
    }

    const trackingId = TrackingId.NewsletterUnsubscribeClick;

    const payload: NewsletterUnsubscribeHit = {
      user_ID: userId,
      newsletter_email: newsletterEmail,
      edition_id: editionId || null,
      element_name: TrackingElement.NewsletterUnsubscribeClick,
      action
    };

    this.track(trackingId, payload);
    return null;
  };
}

const analyticsService = new AnalyticsService();
export default analyticsService;
